import * as React from 'react';
import {
  List,
  TextField,
  FunctionField,
  SearchInput,
  Button,
  useTranslate,
  useNotify,
  useRefresh,
} from 'react-admin';

// Components
import EmptyList from '../components/misc/EmptyList';
import ResizableDatagrid from '../components/table/ResizableDatagrid';
import { apiPost } from '../services/apiClient';

// Libs
import moment from 'moment';

// Entity
const entity = 'deleteUserRequests';

const deleteReasonLabel = (reason, translate) => {
  switch (String(reason)) {
    case '1':
      return translate('deleteUserRequests.reasons.another_account', {
        _: 'Başka bir hesabım var',
      });
    case '2':
      return translate('deleteUserRequests.reasons.privacy_concerns', {
        _: 'Gizlilik kaygılarım var',
      });
    case '3':
      return translate('deleteUserRequests.reasons.unused_account', {
        _: 'Kullanılmayan/aktif olmayan hesap',
      });
    case '4':
      return translate('deleteUserRequests.reasons.other_reason', { _: 'Diğer bir sebebim var' });
    default:
      return reason ? String(reason) : '—';
  }
};

const formatRequestedAt = (value) => {
  if (value == null || value === '') return '—';
  const m =
    typeof value === 'number' || /^\d+$/.test(String(value))
      ? moment(Number(value))
      : moment(value);
  return m.isValid() ? m.format('LLLL') : '—';
};

const OpenUserButton = ({ record }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = React.useState(false);
  const uid = record?.uid || record?.id;

  const handleOpen = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uid || busy) return;
    setBusy(true);
    try {
      const res = await apiPost(`users/${encodeURIComponent(uid)}/restore`, {});
      const fb = res?.firebase;
      if (fb && fb.ok === false) {
        notify(
          translate('deleteUserRequests.open_partial', {
            _: 'DB açıldı; Firebase yazılamadı: %{reason}',
            reason: fb.reason || 'error',
          }),
          { type: 'warning' },
        );
      } else if (fb?.skipped) {
        notify(
          translate('deleteUserRequests.open_ok_no_firebase_doc', {
            _: 'Kullanıcı açıldı (Firestore dokümanı yoktu)',
          }),
          { type: 'info' },
        );
      } else {
        notify(
          translate('deleteUserRequests.open_ok', {
            _: 'Kullanıcı açıldı (DB + Firebase)',
          }),
          { type: 'success' },
        );
      }
      if (typeof refresh === 'function') {
        refresh();
      }
    } catch (err) {
      notify(
        err?.message ||
          translate('deleteUserRequests.open_fail', {
            _: 'Kullanıcı açılamadı',
          }),
        { type: 'error' },
      );
    } finally {
      setBusy(false);
    }
  };

  if (!uid) return null;

  return (
    <Button
      onClick={handleOpen}
      disabled={busy}
      label={
        busy
          ? translate('deleteUserRequests.opening', { _: 'Açılıyor…' })
          : translate('deleteUserRequests.open_user', { _: 'Kullanıcıyı Aç' })
      }
    />
  );
};

export const DeleteUserRequestList = (props) => {
  const translate = useTranslate();
  const filters = [
    <SearchInput
      key="q"
      source="q"
      alwaysOn
      placeholder={translate('deleteUserRequests.search_placeholder', {
        _: 'E-posta, isim veya telefon',
      })}
    />,
  ];

  return (
    <List
      {...props}
      empty={<EmptyList entity={entity} />}
      exporter={false}
      filters={filters}
      sort={{ field: 'createdate', order: 'DESC' }}
      perPage={50}
      title={translate('deleteUserRequests.title', { _: 'Hesap Silme Talepleri' })}
    >
      <ResizableDatagrid
        storageKey="gk-datagrid-widths:deleteUserRequests"
        bulkActionButtons={false}
        rowClick={false}
      >
        <TextField source="uid" label={'ID'} />
        <FunctionField
          source="namesurname"
          label={translate('deleteUserRequests.name', { _: 'Ad Soyad' })}
          render={(r) => `${r.name || ''} ${r.surname || ''}`.trim() || '—'}
        />
        <TextField source="email" label={translate('deleteUserRequests.email', { _: 'E-posta' })} />
        <TextField
          source="mobile"
          label={translate('deleteUserRequests.mobile', { _: 'Telefon' })}
        />
        <FunctionField
          source="reason"
          label={translate('deleteUserRequests.reason', { _: 'Silme Nedeni' })}
          render={(r) => deleteReasonLabel(r.reason, translate)}
        />
        <FunctionField
          source="createdate"
          label={translate('deleteUserRequests.requested_at', { _: 'Talep Tarihi' })}
          render={(r) => formatRequestedAt(r.createdate)}
        />
        <FunctionField label="" render={(r) => <OpenUserButton record={r} />} />
      </ResizableDatagrid>
    </List>
  );
};
